import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check, ListChecks, Users as UsersIcon } from "lucide-react";

/**
 * AudiencePicker — searchable dropdown with type badge for selecting a
 * contact list (audience) in the campaign builder.
 *
 * Props:
 *  - lists: [{id, name, contact_count, description?, owner_name?}]
 *  - value: selected list id
 *  - onChange: (id) => void
 *  - testid: data-testid for the trigger button
 */
export default function AudiencePicker({ lists = [], value, onChange, testid = "audience-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | empty | populated
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const selected = useMemo(() => lists.find((l) => l.id === value) || null, [lists, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lists.filter((l) => {
      if (typeFilter === "empty" && (l.contact_count || 0) > 0) return false;
      if (typeFilter === "populated" && (l.contact_count || 0) === 0) return false;
      if (!q) return true;
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.description || "").toLowerCase().includes(q) ||
        (l.owner_name || "").toLowerCase().includes(q)
      );
    });
  }, [lists, query, typeFilter]);

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testid}-root`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="cf-input flex items-center justify-between gap-2 text-left"
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 min-w-0">
          <ListChecks size={13} className="flex-shrink-0" style={{ color: "var(--cf-text-dim)" }} />
          {selected ? (
            <span className="truncate">
              <span className="cf-text-base">{selected.name}</span>
              <span className="ml-2 cf-text-dim font-mono text-[10px]">
                · {selected.contact_count || 0} contacts
              </span>
            </span>
          ) : (
            <span className="cf-text-dim">Choose an audience…</span>
          )}
        </span>
        <ChevronDown
          size={13}
          className="flex-shrink-0 transition-transform"
          style={{
            color: "var(--cf-text-dim)",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </button>

      {open && (
        <div
          className="cf-combobox-pop absolute z-50 left-0 right-0 mt-1 max-h-[360px] flex flex-col"
          data-testid={`${testid}-popover`}
        >
          {/* Search */}
          <div className="p-2 border-b" style={{ borderColor: "var(--cf-border)" }}>
            <div className="relative">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2"
                style={{ color: "var(--cf-text-dim)" }}
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search audience by name, description, owner…"
                className="cf-input"
                style={{ paddingLeft: 28, fontSize: 12 }}
                data-testid={`${testid}-search`}
              />
            </div>
            {/* Type segmented filter */}
            <div className="flex items-center gap-1 mt-2" data-testid={`${testid}-type-filter`}>
              {[
                { k: "all", label: "All" },
                { k: "populated", label: "With contacts" },
                { k: "empty", label: "Empty" },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setTypeFilter(t.k)}
                  data-testid={`${testid}-type-${t.k}`}
                  className="px-2 py-1 border text-[10px] font-mono uppercase tracking-widest transition-colors"
                  style={{
                    borderColor: typeFilter === t.k ? "var(--cf-text)" : "var(--cf-border)",
                    color: typeFilter === t.k ? "var(--cf-text)" : "var(--cf-text-dim)",
                    background: typeFilter === t.k ? "var(--cf-highlight)" : "transparent",
                  }}
                >
                  {t.label}
                </button>
              ))}
              <span className="ml-auto font-mono text-[10px] cf-text-dim">
                {filtered.length}/{lists.length}
              </span>
            </div>
          </div>

          {/* Options */}
          <div className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center font-mono text-[11px] uppercase tracking-widest cf-text-dim">
                // no matching audience
              </div>
            ) : (
              filtered.map((l) => {
                const isSelected = l.id === value;
                const isEmpty = (l.contact_count || 0) === 0;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => pick(l.id)}
                    data-testid={`${testid}-option-${l.id}`}
                    className="w-full px-3 py-2.5 flex items-start gap-3 text-left transition-colors"
                    style={{
                      background: isSelected ? "var(--cf-highlight)" : "transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "var(--cf-row-hover)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span
                      className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border flex-shrink-0 mt-0.5"
                      style={{
                        borderColor: "var(--cf-border)",
                        color: "var(--cf-text-muted)",
                        background: "var(--cf-surface-2)",
                      }}
                    >
                      LIST
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="cf-text-base truncate font-medium text-sm">{l.name}</span>
                        {isSelected && (
                          <Check size={12} style={{ color: "var(--cf-accent)" }} />
                        )}
                      </span>
                      <span className="block font-mono text-[10px] mt-0.5 flex items-center gap-1.5">
                        <UsersIcon size={9} className="opacity-70" />
                        <span style={{ color: isEmpty ? "var(--cf-warning)" : "var(--cf-text-dim)" }}>
                          {l.contact_count || 0} contact{(l.contact_count || 0) === 1 ? "" : "s"}
                        </span>
                        {l.owner_name && (
                          <span className="cf-text-faint">· by {l.owner_name}</span>
                        )}
                      </span>
                      {l.description && (
                        <span className="block text-[11px] mt-0.5 truncate cf-text-dim">
                          {l.description}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div
            className="px-3 py-2 border-t font-mono text-[10px] uppercase tracking-widest cf-text-faint"
            style={{ borderColor: "var(--cf-border)" }}
          >
            // type to search · filter by status
          </div>
        </div>
      )}
    </div>
  );
}
